import { useEffect, useState, type FormEvent } from "react";
import { INJURY_TYPE_OPTIONS, type PlayerInjuryPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bandage } from "lucide-react";

// 146 -- Injury & concussion management. Staff-only (admin/coach); an active
// injury suppresses the player in selection, concussion is a hard block until
// cleared. A 403 (not staff) hides the section.

export function PlayerInjurySection({ playerId }: { playerId: string }) {
  const [injuries, setInjuries] = useState<PlayerInjuryPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [type, setType] = useState<"general" | "concussion">("general");
  const [injuryLabel, setInjuryLabel] = useState(""); // 156 -- specific label
  const [injuredOn, setInjuredOn] = useState("");
  const [returnToTrain, setReturnToTrain] = useState(""); // 156
  const [returnToPlay, setReturnToPlay] = useState(""); // 156 -- estimate (general)
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);

  function load() {
    api
      .getPlayerInjuries(playerId)
      .then((res) => setInjuries(res.injuries))
      .catch((e) => {
        if (e instanceof api.ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof api.ApiError ? e.message : "Failed to load injuries");
      })
      .finally(() => setLoading(false));
  }

  useEffect(load, [playerId]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!injuredOn) return;
    setAdding(true);
    setError(null);
    try {
      const res = await api.createPlayerInjury(playerId, {
        type,
        injury_type: injuryLabel.trim() || null,
        injured_on: injuredOn,
        return_to_train_on: returnToTrain || null,
        earliest_return_on: type === "general" && returnToPlay ? returnToPlay : null,
        note: note.trim() || null,
      });
      setInjuries((prev) => [res.injury, ...prev]);
      setType("general");
      setInjuryLabel("");
      setInjuredOn("");
      setReturnToTrain("");
      setReturnToPlay("");
      setNote("");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to record injury");
    } finally {
      setAdding(false);
    }
  }

  async function handleClear(injuryId: string) {
    const clearedBy = window.prompt("Who cleared the player to return (e.g. doctor name)?");
    if (!clearedBy) return;
    // 156 -- a clearance note validates "clear to play"; optional but recommended.
    const noteUrl = window.prompt("Doctor's clearance note URL (optional -- required for official clear-to-play):", "");
    setError(null);
    try {
      const res = await api.clearPlayerInjury(playerId, injuryId, {
        cleared_by: clearedBy,
        clearance_note_url: noteUrl && noteUrl.trim() ? noteUrl.trim() : null,
      });
      setInjuries((prev) => prev.map((i) => (i.id === injuryId ? res.injury : i)));
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to clear injury");
    }
  }

  if (forbidden) return null;

  const active = injuries.filter((i) => i.active);
  const cleared = injuries.filter((i) => !i.active);

  return (
    <Card className="max-w-lg" data-region="player-injuries">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bandage className="h-4 w-4" />
          Injuries & Concussion
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="flex flex-col gap-4">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            {active.length > 0 && (
              <div className="flex flex-col gap-2" data-region="active-injuries">
                {active.map((inj) => (
                  <div key={inj.id} className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={
                            inj.type === "concussion"
                              ? "bg-red-100 text-red-800"
                              : "bg-amber-100 text-amber-800"
                          }
                        >
                          {inj.type === "concussion" ? "Concussion" : "Injured"}
                        </Badge>
                        {inj.injury_type && <span className="text-sm font-medium">{inj.injury_type}</span>}
                        <span className="text-sm text-muted-foreground">since {inj.injured_on}</span>
                      </div>
                      {inj.type === "concussion" && inj.earliest_return_on && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Earliest return {inj.earliest_return_on} (21-day stand-down) — and only once doctor-cleared.
                        </p>
                      )}
                      {inj.type === "general" && inj.earliest_return_on && (
                        <p className="mt-1 text-xs text-muted-foreground">Return to play (est.) {inj.earliest_return_on}</p>
                      )}
                      {inj.return_to_train_on && (
                        <p className="mt-1 text-xs text-muted-foreground">Return to train (est.) {inj.return_to_train_on}</p>
                      )}
                      {inj.note && <p className="mt-1 text-xs text-muted-foreground">{inj.note}</p>}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleClear(inj.id)} data-action="clear-injury">
                      Clear
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {active.length === 0 && (
              <p className="text-sm text-muted-foreground">No active injuries.</p>
            )}

            {/* Record a new injury */}
            <form onSubmit={handleAdd} className="flex flex-col gap-3 rounded-md border border-dashed border-border p-3" data-form="add-injury">
              <p className="text-sm font-medium">Record an injury</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="injury-type">Type</Label>
                  <select
                    id="injury-type"
                    data-field="injury-type"
                    value={type}
                    onChange={(e) => setType(e.target.value as "general" | "concussion")}
                    className="flex h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="general">General injury</option>
                    <option value="concussion">Concussion</option>
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="injured-on">Injured on</Label>
                  <Input
                    id="injured-on"
                    data-field="injured-on"
                    type="date"
                    value={injuredOn}
                    onChange={(e) => setInjuredOn(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="injury-label">Injury (specific)</Label>
                <Input
                  id="injury-label"
                  data-field="injury-label"
                  list="injury-type-options"
                  value={injuryLabel}
                  onChange={(e) => setInjuryLabel(e.target.value)}
                  placeholder="e.g. Hamstring strain"
                />
                <datalist id="injury-type-options">
                  {INJURY_TYPE_OPTIONS.map((o) => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="return-train">Return to train (est.)</Label>
                  <Input id="return-train" data-field="return-train" type="date" value={returnToTrain} onChange={(e) => setReturnToTrain(e.target.value)} />
                </div>
                {type === "general" && (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="return-play">Return to play (est.)</Label>
                    <Input id="return-play" data-field="return-play" type="date" value={returnToPlay} onChange={(e) => setReturnToPlay(e.target.value)} />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="injury-note">Note</Label>
                <Input
                  id="injury-note"
                  data-field="injury-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Additional detail"
                />
              </div>
              <Button type="submit" size="sm" disabled={adding} data-action="add-injury">
                {adding ? "Recording…" : "Record injury"}
              </Button>
            </form>

            {cleared.length > 0 && (
              <details data-region="injury-history">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Past injuries ({cleared.length})
                </summary>
                <div className="mt-2 flex flex-col gap-1">
                  {cleared.map((inj) => (
                    <p key={inj.id} className="text-xs text-muted-foreground">
                      {inj.injury_type || (inj.type === "concussion" ? "Concussion" : "Injury")} {inj.injured_on} → cleared {inj.cleared_on}
                      {inj.cleared_by ? ` by ${inj.cleared_by}` : ""}
                      {inj.clear_to_play ? " (note on file)" : ""}
                    </p>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
